import React from "react";
import t from "../../../../../../i18n";
import { colors } from "@ui/Theme";
import { lightest } from "@ui";
import { useQOI } from "../../../useQOI";
import { aggregateSerieValues } from "../../../utils/series";
import { REPORTING_POINT_METADATA } from "../../../../../Project/Chart/PointMetadata";
import { getPointProjectCount } from "../../../utils/projectCount";
import type { PerformanceReporting } from "../../../../../../api/useReporting";

export const MEASURED_SERIE_ID = "FleetPerformanceEvolutionMeasured";
export const EXPECTED_SERIE_ID = "FleetPerformanceEvolutionExpected";

const measuredSerie: Highcharts.SeriesLineOptions = {
  id: MEASURED_SERIE_ID,
  name: t("reporting.fleet_performance.chart.measured"),
  yAxis: "performance-measured-expected",
  type: "line",
  color: colors.raw.lightNitrogen600,
  marker: {
    symbol: "circle",
    lineColor: colors.raw.lightNitrogen600,
    fillColor: lightest(colors.raw.lightNitrogen600),
    lineWidth: 2,
  },
  data: [],
  replicateInNavigator: true,
  showInLegend: true,
  legendIndex: 0,
};

const expectedSerie: Highcharts.SeriesLineOptions = {
  id: EXPECTED_SERIE_ID,
  name: t("reporting.fleet_performance.chart.expected"),
  yAxis: "performance-measured-expected",
  type: "line",
  color: colors.raw.chlorine600,
  marker: {
    symbol: "circle",
    lineColor: colors.raw.chlorine600,
    fillColor: lightest(colors.raw.chlorine600),
    lineWidth: 2,
  },
  data: [],
  replicateInNavigator: true,
  showInLegend: true,
  legendIndex: 1,
};

const aggregateMeasuredOrExpected =
  (serieId?: string) =>
  (currentValue: number | null, measured: number | null, expected: number | null) => {
    const value = serieId === EXPECTED_SERIE_ID ? expected : measured;
    return currentValue && value ? currentValue + value : value;
  };

export function useSeries(reporting: PerformanceReporting) {
  const [qoi] = useQOI(reporting);

  return React.useMemo(() => {
    if (!qoi || !reporting) return [measuredSerie, expectedSerie];
    const qois = Object.values(reporting.qoiById).filter(({ name }) => name === qoi.name);
    const qoiIds = new Set(qois.map(({ id }) => id));
    const qoiProjectIds = new Set(qois.map(({ projectId }) => projectId));

    return reporting.periods.reduce<Highcharts.SeriesLineOptions[]>(
      (acc, { period, driftedProjectIds, values }) => {
        const pointProjectCount = getPointProjectCount(
          qoiIds,
          qoiProjectIds,
          values,
          driftedProjectIds,
        );
        return acc.map(serie => ({
          ...serie,
          data: [
            ...(serie.data ?? []),
            {
              x: new Date(period).getTime(),
              y: aggregateSerieValues(
                values,
                aggregateMeasuredOrExpected(serie.id),
                value => reporting.qoiById[value.qoiId]?.name === qoi.name,
              ),
              custom: {
                type: REPORTING_POINT_METADATA,
                ...pointProjectCount,
              },
            },
          ],
        }));
      },
      [measuredSerie, expectedSerie],
    );
  }, [reporting, qoi]);
}
